import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Tilt from "react-parallax-tilt";
import { ChevronLeft, ChevronRight, Sparkles, Crown, Check } from "lucide-react";
import { WEDDING_PALACE, HOTEL } from "../data/site";

function HallSlider({ images }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((p) => (p + 1) % images.length), 5000);
    return () => clearInterval(id);
  }, [images.length]);

  const next = () => setI((p) => (p + 1) % images.length);
  const prev = () => setI((p) => (p - 1 + images.length) % images.length);

  return (
    <div className="relative aspect-[4/5] sm:aspect-[5/6] overflow-hidden rounded-3xl bg-muted shadow-2xl shadow-[#7A1A24]/20">
      {images.map((src, idx) => (
        <img
          key={src}
          src={src}
          alt={`Wedding mandap and hall view ${idx + 1}`}
          loading="lazy"
          className={`absolute inset-0 h-full w-full object-cover transition-all duration-1000 ${
            idx === i ? "opacity-100 scale-100" : "opacity-0 scale-105"
          }`}
        />
      ))}
      {/* Cinematic gold sweep */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#1A0608]/85 to-transparent" />

      <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-[#D4AF37]/95 text-[#1A1515] px-3 py-1 text-[10px] tracking-[0.2em] uppercase font-semibold">
        <Crown size={10} /> Kalyana Mandap
      </div>

      <button
        onClick={prev}
        aria-label="Previous mandap image"
        data-testid="mandap-slider-prev"
        className="absolute left-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full grid place-items-center bg-white/90 hover:bg-white text-[#1A1515] shadow-lg"
      >
        <ChevronLeft size={18} />
      </button>
      <button
        onClick={next}
        aria-label="Next mandap image"
        data-testid="mandap-slider-next"
        className="absolute right-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full grid place-items-center bg-white/90 hover:bg-white text-[#1A1515] shadow-lg"
      >
        <ChevronRight size={18} />
      </button>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
        {images.map((src, idx) => (
          <button
            key={src}
            onClick={() => setI(idx)}
            aria-label={`Go to slide ${idx + 1}`}
            className={`h-1.5 rounded-full transition-all ${
              idx === i ? "w-8 bg-[#D4AF37]" : "w-1.5 bg-white/60"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export default function WeddingPalace() {
  return (
    <section
      id="mandap"
      data-testid="mandap-section"
      className="section-padding bg-background relative overflow-hidden"
    >
      <div aria-hidden className="absolute -top-32 -left-20 h-96 w-96 rounded-full bg-[#D4AF37]/8 blur-[120px]" />
      <div aria-hidden className="absolute -bottom-32 -right-20 h-96 w-96 rounded-full bg-[#7A1A24]/10 blur-[120px]" />

      <div className="container-x relative">
        <div className="max-w-3xl">
          <span className="eyebrow flex items-center gap-2">
            <Crown size={12} /> The Kalyana Mandap
          </span>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-foreground">
            A royal mandap,<br />
            <span className="italic text-[#7A1A24] dark:text-[#D4AF37]">made for once-in-a-lifetime moments.</span>
          </h2>
          <div className="mt-6 ornament-divider max-w-[220px]">
            <span className="text-lg">❖</span>
          </div>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
            {WEDDING_PALACE.intro}
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Slider column */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-6"
          >
            <Tilt
              tiltMaxAngleX={3}
              tiltMaxAngleY={3}
              glareEnable
              glareMaxOpacity={0.15}
              glareColor="#D4AF37"
              transitionSpeed={1600}
            >
              <HallSlider images={WEDDING_PALACE.images} />
            </Tilt>

            {/* Capacity card */}
            <div className="mt-6 glass-card rounded-3xl p-6">
              <div className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-4">
                Venue Capacity
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                {WEDDING_PALACE.capacity.map((c) => (
                  <div key={c.label}>
                    <div className="font-display text-lg text-foreground">{c.value}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{c.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Bullets column */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-6 lg:pt-4"
          >
            <ul className="space-y-5">
              {WEDDING_PALACE.bullets.map((b, i) => (
                <motion.li
                  key={b.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.55 }}
                  data-testid={`mandap-bullet-${i}`}
                  className="flex gap-4 p-5 rounded-2xl border border-border bg-card hover:border-[#D4AF37]/60 hover:-translate-y-0.5 transition-all duration-300"
                >
                  <div className="h-10 w-10 shrink-0 rounded-xl bg-[#D4AF37]/15 text-[#A8862B] dark:text-[#E6C555] grid place-items-center">
                    <Check size={18} strokeWidth={2.4} />
                  </div>
                  <div>
                    <div className="font-display text-lg text-foreground">{b.title}</div>
                    <div className="text-sm text-muted-foreground leading-relaxed mt-1">
                      {b.desc}
                    </div>
                  </div>
                </motion.li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={HOTEL.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mandap-book-venue"
                className="btn-primary"
              >
                <Sparkles size={16} />
                Book Venue on WhatsApp
              </a>
              <a
                href={`tel:${HOTEL.phoneE164}`}
                data-testid="mandap-call"
                className="btn-gold"
              >
                Call {HOTEL.phoneFormatted}
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
